<script setup lang="ts">
  import { ListboxFilter } from 'reka-ui'
  import { IconFilter, IconSearch } from '@gat-ship/icons'

  /**
   * The shared search box for a list step: a `ListboxFilter` (so ↑/↓ keep
   * working while focus is in the input) with a leading search icon and the
   * step flow's input styling. Rendered inside each step's `<Command>`, where
   * the reka `Listbox` context reaches it through provide/inject.
   *
   * Bound to the caller's own query ref (not the `Command`'s internal filter),
   * so the rows shown are exactly what the step passes — already client-filtered
   * or fresh from a server search.
   *
   * The `enterkeyhint`/`autocapitalize`/`autocorrect`/`spellcheck` attributes
   * tune the on-screen keyboard for a filter box (search-style return key, no
   * autocapitalise/autocorrect fighting codes like `NSBER`). We deliberately do
   * NOT set `inputmode="search"` — on iOS that swaps in a variant layout that
   * splits the space bar with a `.` key. iOS still picks the keyboard
   * *language/layout* from the device — the web can't override that. These
   * attributes fall through reka's `ListboxFilter` onto the underlying `<input>`.
   */
  defineProps<{
    modelValue: string
    placeholder?: string
    isFilter?: boolean
    /**
     * Mobile keyboard is up — show a trailing "Done" that blurs the field to
     * dismiss the keyboard. The native soft keyboard carries no dismiss key
     * here, and we hide the header/footer while it's open, so this is the
     * discoverable way back out.
     */
    keyboardOpen?: boolean
  }>()

  const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

  /** Blur the focused field to dismiss the soft keyboard. */
  function dismissKeyboard() {
    ;(document.activeElement as HTMLElement | null)?.blur()
  }
</script>

<template>
  <div class="flex items-center gap-2">
    <div class="relative flex-1">
      <component
        :is="isFilter ? IconFilter : IconSearch"
        class="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2"
      />
      <ListboxFilter
        :model-value="modelValue"
        auto-focus
        :placeholder="placeholder"
        enterkeyhint="search"
        autocapitalize="off"
        autocorrect="off"
        autocomplete="off"
        spellcheck="false"
        class="border-input focus-visible:border-ring focus-visible:ring-ring/50 outline-hidden h-11 w-full rounded-lg border bg-transparent pl-10 pr-3 text-sm focus-visible:ring-[3px]"
        @update:model-value="emit('update:modelValue', String($event ?? ''))"
      />
    </div>
    <!-- Native soft keyboards can't carry a custom dismiss button, so we render
         our own, shown only while the keyboard is up. `@pointerdown.prevent`
         keeps the tap from shifting focus first, so the blur is ours to time. -->
    <button
      v-if="keyboardOpen"
      type="button"
      class="text-primary shrink-0 px-2 py-1 text-sm font-medium"
      @pointerdown.prevent="dismissKeyboard"
    >
      Done
    </button>
  </div>
</template>
